import { useState, useEffect } from 'react';
import { apiService } from '@/lib/api';
import { Client } from '@/types/client';

interface IntegrationData {
  system: string;
  token: string;
  usuario: string;
  senha: string;
}

interface SituacoesMapping {
  'ativo': string[];
  'inadimplente_carencia': string[];
  'inadimplente_revistoria': string[];
  'inadimplente_ate_30_dias': string[];
  'inadimplente_ate_60_dias': string[];
  'inadimplente_ate_90_dias': string[];
  'inativo': string[];
  'cancelado': string[];
}

export const useHinovaIntegration = (client?: Client) => {
  // Estados do fluxo
  const [step, setStep] = useState<'initial' | 'credentials' | 'mapping'>('initial');
  const [selectedSystem, setSelectedSystem] = useState(client?.system_management || '');
  const [integrationActive, setIntegrationActive] = useState(false);
  
  // Estados de dados
  const [integrationData, setIntegrationData] = useState<IntegrationData>({
    system: '',
    token: '',
    usuario: '',
    senha: ''
  });
  const [authToken, setAuthToken] = useState('');
  const [tiposBoleto, setTiposBoleto] = useState<any[]>([]);
  const [situacoesVeiculo, setSituacoesVeiculo] = useState<any[]>([]);
  
  // Estados de seleção
  const [selectedTiposBoleto, setSelectedTiposBoleto] = useState<string[]>([]);
  const [situacoesMapping, setSituacoesMapping] = useState<SituacoesMapping>({
    'ativo': [],
    'inadimplente_carencia': [],
    'inadimplente_revistoria': [],
    'inadimplente_ate_30_dias': [],
    'inadimplente_ate_60_dias': [],
    'inadimplente_ate_90_dias': [],
    'inativo': [],
    'cancelado': []
  });
  
  // Estados de loading e feedback
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Sincronizar selectedSystem com system_management do cliente
  useEffect(() => {
    if (client?.system_management) {
      setSelectedSystem(client.system_management);
      console.log('🔄 [useHinovaIntegration] Sistema de gestão atualizado:', client.system_management);
    }
  }, [client?.system_management]);

  // Verificar se já existe integração ativa
  useEffect(() => {
    if (client?.id) {
      checkExistingIntegration();
    }
  }, [client?.id]);

  const checkExistingIntegration = async () => {
    try {
      console.log('🔍 Verificando integração existente para cliente:', client!.id);
      const response = await apiService.get(`/integrations/client/${client!.id}`);
      console.log('📡 Resposta da verificação:', response);
      
      // Debug detalhado da estrutura da resposta
      console.log('🔍 Debug da estrutura:');
      console.log('- response existe?', !!response);
      console.log('- response.integration existe?', !!response?.integration);
      console.log('- response.integration.is_active?', response?.integration?.is_active);
      console.log('- Tipo do is_active:', typeof response?.integration?.is_active);
      
      if (response?.integration?.is_active === true) {
        console.log('✅ Integração ativa encontrada!');
        console.log('Dados da integração:', {
          id: response.integration.id,
          system_type: response.integration.system_type,
          is_active: response.integration.is_active,
          has_token: !!response.integration.token,
          has_user_token: !!response.integration.user_token
        });
        
        setIntegrationActive(true);
        setSelectedSystem(response.integration.system_type);
        setAuthToken(response.integration.user_token || response.integration.token);
        setStep('mapping');
        
        // Carregar dados para mapeamento
        console.log('📊 Carregando dados de mapeamento...');
        await loadMappingData();
        
        // Carregar mapeamentos existentes
        console.log('📊 Carregando mapeamentos existentes...');
        await loadExistingMappings();
      } else {
        console.log('❌ Nenhuma integração ativa encontrada');
        console.log('Motivo:', {
          hasResponse: !!response,
          hasIntegration: !!response?.integration,
          isActive: response?.integration?.is_active,
          isActiveType: typeof response?.integration?.is_active
        });
        console.log('Resposta completa:', response);
      }
    } catch (error) {
      console.error('❌ Erro ao verificar integração existente:', error);
      console.log('Nenhuma integração ativa encontrada');
    }
  };

  const startIntegration = () => {
    if (!selectedSystem) {
      setError('Selecione um sistema de gestão');
      return false;
    }
    setIntegrationData(prev => ({ ...prev, system: selectedSystem }));
    setStep('credentials');
    setError('');
    return true;
  };

  const authenticate = async () => {
    if (!integrationData.token || !integrationData.usuario || !integrationData.senha) {
      setError('Preencha token, usuário e senha para iniciar');
      return false;
    }

    setLoading(true);
    setError('');
    
    try {
      console.log('🔐 Iniciando autenticação com Hinova...');
      console.log('Dados enviados:', {
        token: integrationData.token ? `${integrationData.token.substring(0, 10)}...` : 'VAZIO',
        usuario: integrationData.usuario,
        senha: '***'
      });

      // Chamada de autenticação
      const response = await apiService.post('/hinova/authenticate', {
        token: integrationData.token,
        usuario: integrationData.usuario,
        senha: integrationData.senha
      });

      console.log('📡 Resposta da autenticação:', response);

      // Verificar diferentes formatos de resposta
      let userToken = null;
      let isSuccess = false;

      if (response.data?.success && response.data?.token) {
        userToken = response.data.token;
        isSuccess = true;
        console.log('✅ Token encontrado em response.data.token');
      } else if (response.data?.mensagem === 'OK' && response.data?.token_usuario) {
        userToken = response.data.token_usuario;
        isSuccess = true;
        console.log('✅ Token encontrado em response.data.token_usuario');
      } else if (response.data?.token) {
        userToken = response.data.token;
        isSuccess = true;
        console.log('✅ Token encontrado em response.data.token (formato direto)');
      } else if (response.data?.token_usuario) {
        userToken = response.data.token_usuario;
        isSuccess = true;
        console.log('✅ Token encontrado em response.data.token_usuario (formato direto)');
      }

      if (isSuccess && userToken) {
        console.log('✅ Autenticação bem-sucedida! Token recebido');
        setAuthToken(userToken);
        
        // Salvar integração no banco
        console.log('💾 Salvando integração no banco...');
        await apiService.post('/integrations/save', {
          client_id: client!.id,
          system_type: selectedSystem,
          token: integrationData.token, // Token original
          username: integrationData.usuario,
          password: integrationData.senha,
          user_token: userToken, // Token de usuário autenticado
          is_active: true
        });

        setIntegrationActive(true);
        setStep('mapping');
        setMessage('Integração iniciada com sucesso!');
        
        // Carregar dados para mapeamento
        console.log('📊 Carregando dados para mapeamento...');
        await loadMappingData(userToken);
        return true;
      } else {
        console.error('❌ Resposta inválida:', response.data);
        const errorMsg = response.data?.error || 'Token de acesso não recebido';
        setError(errorMsg);
        return false;
      }
    } catch (error: any) {
      console.error('❌ Erro na autenticação:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Erro na autenticação';
      setError(errorMsg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loadExistingMappings = async () => {
    try {
      console.log('🔍 Carregando mapeamentos existentes...');
      const response = await apiService.get(`/financial-settings/client/${client!.id}`);
      
      if (response.success && response.data) {
        const data = response.data;
        console.log('📊 Dados carregados do backend:', data);
        
        // Carregar tipos de boleto selecionados
        if (Array.isArray(data.tipos_boleto)) {
          setSelectedTiposBoleto(data.tipos_boleto);
        }
        
        // Carregar mapeamentos de situações (agora suporta arrays)
        const mapping: SituacoesMapping = {
          'ativo': Array.isArray(data.ativo) ? data.ativo : (data.ativo ? [data.ativo] : []),
          'inadimplente_carencia': Array.isArray(data.inadimplente_carencia) ? data.inadimplente_carencia : (data.inadimplente_carencia ? [data.inadimplente_carencia] : []),
          'inadimplente_revistoria': Array.isArray(data.inadimplente_revistoria) ? data.inadimplente_revistoria : (data.inadimplente_revistoria ? [data.inadimplente_revistoria] : []),
          'inadimplente_ate_30_dias': Array.isArray(data.inadimplente_ate_30_dias) ? data.inadimplente_ate_30_dias : (data.inadimplente_ate_30_dias ? [data.inadimplente_ate_30_dias] : []),
          'inadimplente_ate_60_dias': Array.isArray(data.inadimplente_ate_60_dias) ? data.inadimplente_ate_60_dias : (data.inadimplente_ate_60_dias ? [data.inadimplente_ate_60_dias] : []),
          'inadimplente_ate_90_dias': Array.isArray(data.inadimplente_ate_90_dias) ? data.inadimplente_ate_90_dias : (data.inadimplente_ate_90_dias ? [data.inadimplente_ate_90_dias] : []),
          'inativo': Array.isArray(data.inativo) ? data.inativo : (data.inativo ? [data.inativo] : []),
          'cancelado': Array.isArray(data.cancelado) ? data.cancelado : (data.cancelado ? [data.cancelado] : [])
        };
        
        setSituacoesMapping(mapping);
        console.log('✅ Mapeamentos carregados:', mapping);
      }
    } catch (error: any) {
      console.error('❌ Erro ao carregar mapeamentos existentes:', error);
      // Não definir erro aqui para não interromper o fluxo
    }
  };

  const loadMappingData = async (userToken?: string) => {
    try {
      setLoading(true);
      console.log('📊 Carregando dados para mapeamento...');
      
      // Primeiro verificar se a integração está ativa
      console.log('🔍 Verificando status da integração...');
      const integrationCheck = await apiService.get(`/hinova/check-integration?client_id=${client!.id}`);
      
      if (!integrationCheck.success || !integrationCheck.active) {
        throw new Error('Integração Hinova não configurada ou sem token válido');
      }
      
      if (!integrationCheck.has_token) {
        throw new Error('Token de autenticação não encontrado. Faça login novamente.');
      }
      
      console.log('✅ Integração verificada:', integrationCheck);
      
      // Buscar o token da integração para usar no header
      console.log('🔍 Buscando token da integração...');
      const integrationResponse = await apiService.get(`/integrations/client/${client!.id}`);
      const integration = integrationResponse?.integration;
      
      if (!integration || !integration.user_token) {
        throw new Error('Token de integração não encontrado. Configure a integração novamente.');
      }
      
      console.log('✅ Token da integração encontrado');
      
      // Configurar headers com o token da integração
      const headers = {
        'Authorization': `Bearer ${integration.user_token}`,
        'Content-Type': 'application/json'
      };
      
      // Carregar tipos de boleto
      console.log('🔍 Carregando tipos de boleto...');
      const tiposBoletoResponse = await apiService.post('/hinova/tipos-boleto', {
        client_id: client!.id
      }, { headers });
      
      if (tiposBoletoResponse.success && tiposBoletoResponse.data) {
        setTiposBoleto(tiposBoletoResponse.data);
        console.log('✅ Tipos de boleto carregados:', tiposBoletoResponse.data.length);
      } else {
        console.error('❌ Erro ao carregar tipos de boleto:', tiposBoletoResponse.error);
        throw new Error(`Erro ao carregar tipos de boleto: ${tiposBoletoResponse.error}`);
      }

      // Carregar situações de veículo
      console.log('🔍 Carregando situações de veículo...');
      const situacoesResponse = await apiService.post('/hinova/situacoes-veiculo', {
        client_id: client!.id
      }, { headers });
      
      if (situacoesResponse.success && situacoesResponse.data) {
        // 🔧 VALIDAÇÃO CRÍTICA: Verificar estrutura dos dados antes de definir
        console.log('🔍 [useHinovaIntegration] Dados brutos de situacoesVeiculo:', situacoesResponse.data);
        console.log('🔍 [useHinovaIntegration] Tipo dos dados:', typeof situacoesResponse.data);
        console.log('🔍 [useHinovaIntegration] É array?', Array.isArray(situacoesResponse.data));
        
        if (Array.isArray(situacoesResponse.data)) {
          // Validar cada item do array
          const validSituacoes = situacoesResponse.data.filter((item, index) => {
            const isValid = item && typeof item === 'object';
            if (!isValid) {
              console.error(`❌ [useHinovaIntegration] Item inválido no índice ${index}:`, item);
            }
            return isValid;
          });
          
          console.log('🔍 [useHinovaIntegration] Situações válidas:', validSituacoes.length, 'de', situacoesResponse.data.length);
          setSituacoesVeiculo(validSituacoes);
        } else {
          console.error('❌ [useHinovaIntegration] situacoesResponse.data não é um array:', situacoesResponse.data);
          setSituacoesVeiculo([]);
        }
        
        console.log('✅ Situações de veículo carregadas:', situacoesResponse.data.length);
      } else {
        console.error('❌ Erro ao carregar situações de veículo:', situacoesResponse.error);
        throw new Error(`Erro ao carregar situações de veículo: ${situacoesResponse.error}`);
      }

      console.log('✅ Dados de mapeamento carregados com sucesso!');
    } catch (error: any) {
      console.error('❌ Erro ao carregar dados para mapeamento:', error);
      
      // Tratar erros específicos
      if (error.message?.includes('Integração Hinova não configurada')) {
        setError('Integração Hinova não configurada ou sem token válido. Configure a integração primeiro.');
      } else if (error.message?.includes('Token de autenticação não encontrado')) {
        setError('Token de autenticação não encontrado. Faça login novamente.');
      } else if (error.message?.includes('Token de integração não encontrado')) {
        setError('Token de integração não encontrado. Configure a integração novamente.');
      } else if (error.response?.status === 401) {
        setError('Token inválido ou expirado. Atualize sua conexão com a API Hinova.');
      } else if (error.response?.status === 404) {
        setError('Integração Hinova não encontrada. Configure a integração primeiro.');
      } else {
        setError(error.message || 'Erro ao carregar dados da integração');
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveMapping = async () => {
    setSaving(true);
    setError('');
    
    try {
      console.log('💾 Salvando mapeamentos...');
      console.log('Tipos de boleto selecionados:', selectedTiposBoleto);
      console.log('Mapeamento de situações:', situacoesMapping);
      
      const response = await apiService.post(`/financial-settings/client/${client!.id}/mapping`, {
        tipos_boleto: selectedTiposBoleto,
        situacoes_veiculo_mapping: situacoesMapping
      });

      if (response.success) {
        setMessage('Mapeamentos salvos com sucesso!');
        return true;
      } else {
        throw new Error(response.error || 'Erro ao salvar mapeamentos');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar mapeamentos:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao salvar mapeamentos';
      setError(errorMsg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTipoBoletoChange = (codigo: string, checked: boolean) => {
    console.log(`🔘 Checkbox alterado: ${codigo} = ${checked}`);
    setSelectedTiposBoleto(prev => {
      const newSelection = checked 
        ? [...prev, codigo]
        : prev.filter(c => c !== codigo);
      console.log('Nova seleção:', newSelection);
      return newSelection;
    });
  };

  const handleSituacaoMappingChange = (situacao: keyof SituacoesMapping, selectedValues: string[]) => {
    setSituacoesMapping(prev => ({
      ...prev,
      [situacao]: selectedValues
    }));
  };

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  return {
    // Estados do fluxo
    step,
    selectedSystem,
    integrationActive,
    
    // Estados de dados
    integrationData,
    authToken,
    tiposBoleto,
    situacoesVeiculo,
    selectedTiposBoleto,
    situacoesMapping,
    
    // Estados de feedback
    loading,
    saving,
    message,
    error,
    
    // Setters
    setSelectedSystem,
    setIntegrationData,
    
    // Ações
    startIntegration,
    authenticate,
    saveMapping,
    handleTipoBoletoChange,
    handleSituacaoMappingChange,
    clearMessages,
  };
};
